import { useUpdateItem } from '@api/generics';
import API_ROUTES from '@api/routes';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid
} from '@mui/material';
import {
	MAX_CHARS_AUTHORIZATION,
	stringRequiredMaxCharsValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React from 'react';
import { IPermission } from 'types/authorization';
import { IRowActionDialogProps } from 'types/common';
import { object } from 'yup';

const PermissionEditDialog: React.FC<IRowActionDialogProps> = ({
	onComplete,
	row
}) => {
	const { invokeToastableAction } = useToast();
	const permissionMutation = useUpdateItem(
		API_ROUTES.v1.permissions.update,
		'permissions'
	);

	const formik = useFormik<IPermission>({
		initialValues: row?.row || {},
		validationSchema: object({
			name: stringRequiredMaxCharsValidationScheme(MAX_CHARS_AUTHORIZATION)
		}),
		onSubmit: async (updatedItem: IPermission) => {
			await invokeToastableAction(
				() => permissionMutation.mutateAsync(updatedItem),
				'permissions.action.edit.dialog.toast',
				onComplete
			);
		}
	});

	return (
		<Dialog open maxWidth="xs">
			<DialogTitle>
				{intl.tr('permissions.action.edit.dialog.title')}
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} pt={2} pb={2}>
					<Grid item xs={12}>
						<TextFieldValidation
							id="name"
							formik={formik}
							required
							fullWidth
							autoFocus
							label={intl.tr('fields.permissions.column.name')}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextFieldValidation
							id="description"
							formik={formik}
							fullWidth
							label={intl.tr('fields.permissions.column.description')}
						/>
					</Grid>
					<Grid item xs={12}>
						<Alert severity="warning" sx={{ marginTop: 2, marginBottom: 2 }}>
							{intl.tr('permissions.action.edit.dialog.warning')}
						</Alert>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onComplete}
					data-testid="permission-edit-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="permission-edit-dialog-approve-button"
				>
					{intl.tr('button.save')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default PermissionEditDialog;
